import React from 'react';
import RackImage from 'utils/RackImage';
import { Btn } from 'shared/ui/Btn';
import { Figure } from 'shared/ui/Figure';
import { cn } from 'shared/lib/cn';
import { useSystemSlide } from './useSystemSlide';

// A smaller, local display scale than shared/lib/rackLayout's U_PX (18): several racks
// sit side by side in this preview, so a compact pixel-per-U keeps a full deck on screen
// without a second scroll axis. Deliberately not the shared constant — that one is Task
// 3's own display scale for the single-rack inspector view.
const U_PX = 10;

export function SystemSlide() {
  const s = useSystemSlide();

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-bold">{s.model.title}</h2>
          {/* P4: ink-muted takes no alpha suffix — the mute is already a baked alpha. */}
          <p className="font-body text-sm text-ink-muted">{s.model.subtitle}</p>
        </div>
        <Btn variant="contained" onClick={s.onExport}>Export PPTX</Btn>
      </div>

      <div className="rounded-[var(--radius-2)] border border-hairline/15 bg-surface-raised p-4">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-6">
          {s.model.figures.map((f) => <Figure key={f.label} label={f.label} value={f.value} />)}
        </div>
      </div>

      <div className="flex flex-1 gap-6 overflow-auto rounded-[var(--radius-2)] border border-hairline/15 bg-surface-raised p-4">
        {s.racks.map((rack) => (
          // W8: rackSlidePPTGenerator.js:195 draws a `Rack ${i + 1}` caption for every
          // rack it puts on the slide, at y: PADDING with the rack itself at
          // PADDING + LABEL_H — i.e. ABOVE the rack, which is where this one goes too.
          // The racks previewed here are the deck's own first two (useSystemSlide slices
          // to the same DECK_RACK_LIMIT), so `rack.index + 1` matches the exported file's
          // numbering without any separate bookkeeping.
          <div key={rack.index} className="flex w-24 shrink-0 flex-col gap-1">
            {/* P4: ink-muted takes no alpha suffix — the mute is a baked alpha already. */}
            <span className="text-center font-code text-tiny font-bold text-ink-muted">Rack {rack.index + 1}</span>
            <div
              data-testid="slide-rack"
              className={cn(
                'relative w-full rounded-[2px]',
                // R14: rackSlidePPTGenerator.js:168 suppresses the rack frame/rails for
                // "No Rack" (rackSize 0) — Task 3's ruling R6 made the identical call for
                // Rack View. The units themselves still draw at the effective (42U) size.
                s.showFrame && 'border border-hairline/25',
              )}
              style={{ height: rack.size * U_PX }}
            >
              {rack.units.map(({ position, unit }) => (
                // Controller ruling P3/R2: slot index 0 is the TOP of the rack in both
                // contractual consumers of calculateRackLayout's array
                // (RealisticRackVisualization.jsx `top: index * unitHeight`;
                // rackSlidePPTGenerator.js `y = slotIndex * unitH`, SVG y growing
                // downward). `bottom:` here would mirror what the PPTX actually draws —
                // the same upside-down bug Task 3's review caught in RackView, and worse
                // here because Assumption 4 is that this preview IS what gets exported.
                <div
                  key={`${rack.index}-${position}`}
                  data-testid={`slide-unit-${rack.index}-${position}`}
                  className="absolute inset-x-0"
                  style={{ top: position * U_PX, height: unit.heightU * U_PX }}
                >
                  <RackImage type={unit.type} vendor={s.vendorId} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* R11: rackSlidePPTGenerator.js:173 slices to the first two racks; silently
          previewing more (or silently saying nothing) misrepresents what the file will
          contain, so name the gap when it exists.

          W9: this used to end "Rack View shows all {n}". calculateRackLayout caps the
          layout at TEN racks (RealisticRackVisualization.jsx:52, `Math.min(10, ...)`), so
          for a system needing more, "all" was a claim neither this preview nor Rack View
          can honour. It now reports the number of racks the layout PRODUCED and asserts
          nothing about whether that is every rack the system needs. */}
      {s.isTruncated && (
        <p className="font-body text-xs text-ink-muted">
          The exported deck shows the first two racks; this layout has {s.totalRackCount}.
        </p>
      )}
    </div>
  );
}
